import { Observable } from 'rxjs';
import { Alert } from 'src/app/models/alert';
import { TenantUser } from 'src/app/models/tenantUser';
import { AlertDetailService } from '../alert-detail/alert-detail.service';

// FleetHd.Case CaseReferenceType int values (mirrors the server enum).
export const CaseReferenceType = {
  Alert: 1,
  Issue: 4,
} as const;

// Origin-specific strategy for the case workflow component. The component's
// machinery (status machine, history/comments feed, poll gate, assignment) is
// identical for every origin — only how the active case is looked up, which
// origin the create points at, and who may be assigned differ.
export interface CaseWorkflowContext {
  // Emits the case-list response for this origin (the component takes the first).
  getActiveCase(): Observable<any>;
  // Origin (type + id) for a first-time create. The component assembles the full
  // CreateCaseRequest (assignment / initial status / opening comment) around it.
  createReference(): { referenceTypeId: number; referenceId: string };
  // Filters the tenant user list down to valid assignees for this origin.
  eligibleUsers(users: TenantUser[]): TenantUser[];
}

function intersects(a: string[], b: string[]): boolean {
  const set = new Set(b);
  return a.some(x => set.has(x));
}

// Group match (access control): mirrors the server's HasValues-gated group filter.
// No scope groups => not group-scoped => all eligible. No user groups => user is
// unscoped by group (server returns everything) => eligible. Otherwise require overlap.
function groupEligible(u: TenantUser, scopeGroups: any[]): boolean {
  const scopeGroupIds = (scopeGroups ?? []).map(g => g.id);
  const userGroupIds = (u.groups ?? []).map(g => g.id);
  if (scopeGroupIds.length === 0 || userGroupIds.length === 0) return true;
  return intersects(userGroupIds, scopeGroupIds);
}

// Tag match (My View / strict overlap): empty My View = generalist = sees everything.
// A scoped user does NOT see an untagged alert (alert.tags empty => no overlap) — correct.
function tagEligible(u: TenantUser, alertTags: string[]): boolean {
  const myView = u.alertTags ?? [];
  if (myView.length === 0) return true;
  return intersects(myView, alertTags ?? []);
}

// Alert-origin context. A case may only be assigned to users who can actually SEE
// the alert behind it — otherwise the assignee would never find their work.
// Visibility has two dimensions: group membership (access control) and the user's
// "My View" alert categories/tags. Only users who pass BOTH are offered.
export function alertCaseWorkflowContext(
  service: AlertDetailService,
  alert: Alert,
): CaseWorkflowContext {
  return {
    getActiveCase: () => service.getCaseByAlertId(alert.id),
    createReference: () => ({ referenceTypeId: CaseReferenceType.Alert, referenceId: alert.id }),
    eligibleUsers: users => users.filter(u =>
      groupEligible(u, alert.groups) && tagEligible(u, alert.tags)),
  };
}

// Issue-origin context. The active case is matched by asset + matchKey (= issue id).
// Assignability is asset-group-scoped only — Issues have no tag dimension.
export function issueCaseWorkflowContext(
  service: AlertDetailService,
  assetId: string,
  issueId: string,
  assetGroups: any[],
): CaseWorkflowContext {
  return {
    getActiveCase: () => service.getActiveCase(assetId, issueId),
    createReference: () => ({ referenceTypeId: CaseReferenceType.Issue, referenceId: issueId }),
    eligibleUsers: users => users.filter(u => groupEligible(u, assetGroups)),
  };
}
